import React from "react";
import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Typography, Descriptions, Table, Tag, Card, Space, Button, message } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { IInvoice } from "../../interfaces";
import { formatPrice } from "../../utils/priceUtils";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export const InvoiceShow: React.FC = () => {
  const { queryResult } = useShow<IInvoice>();
  const { data, isLoading, error } = queryResult;
  const invoice = data?.data;

  const downloadPdf = async () => {
    if (!invoice) return;
    
    try {
      message.loading('PDF wird generiert...', 0);
      
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=generatePdf&tx_buchungsmanager_frontend[id]=${invoice.id}`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      message.destroy(); // Remove loading message

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${(invoice as any).fileName || `rechnung-${invoice.id}`}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('PDF wurde heruntergeladen');
      
    } catch (error) {
      message.destroy(); // Remove loading message
      console.error('Error downloading PDF:', error);
      message.error(`Fehler beim PDF-Download: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  };

  // Debug logging
  console.log("InvoiceShow - Loading:", isLoading);
  console.log("InvoiceShow - Error:", error);
  console.log("InvoiceShow - Invoice Data:", invoice);

  if (isLoading) {
    return <div>Laden...</div>;
  }

  if (error) {
    console.error("InvoiceShow - Error loading invoice:", error);
    return <div>Fehler beim Laden der Rechnung: {error.message}</div>;
  }

  if (!invoice) {
    return <div>Rechnung nicht gefunden</div>;
  }

  const columns = [
    {
      title: "Beschreibung",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Menge",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
    },
    {
      title: "Preis",
      dataIndex: "price",
      key: "price",
      width: 150,
      render: (value: number) => formatPrice(value || 0),
    },
    {
      title: "MwSt %",
      dataIndex: "vat",
      key: "vat",
      width: 100,
      render: (value: number) => `${value}%`,
    },
    {
      title: "Gesamt",
      dataIndex: "total",
      key: "total",
      width: 150,
      render: (value: number) => <Text strong>{formatPrice(value || 0)}</Text>,
    },
  ];

  return (
    <Show
      headerButtons={[
        <Button
          key="download-pdf"
          type="primary"
          icon={<FilePdfOutlined />}
          onClick={downloadPdf}
        >
          PDF herunterladen
        </Button>
      ]}
    >
      <div className="invoice-preview">
        {/* Header */}
        <div className="invoice-header">
          <div>
            <Title level={2}>Rechnung</Title>
            {invoice.type === "storno" && <Tag color="red">STORNO</Tag>}
            {invoice.type === "noshow" && <Tag color="orange">NO-SHOW</Tag>}
          </div>
          <div>
            <Text strong>Rechnungsnummer:</Text> {invoice.number}<br />
            <Text strong>Datum:</Text> {dayjs(invoice.date).format("DD.MM.YYYY")}
          </div>
        </div>

        {/* Absender und Empfänger */}
        <Card style={{ marginBottom: 24 }}>
          <Descriptions title="Rechnungsdetails" bordered column={2}>
            {invoice.sender && (
              <Descriptions.Item label="Absender" span={1}>
                <strong>{invoice.sender.company || invoice.sender.name || 'Unbekannt'}</strong><br />
                {invoice.sender.name && <>{invoice.sender.name}<br /></>}
                {invoice.sender.address && <>{invoice.sender.address}<br /></>}
                {(invoice.sender.zip || invoice.sender.city) && <>{invoice.sender.zip} {invoice.sender.city}<br /></>}
                {invoice.sender.email && <>E-Mail: {invoice.sender.email}<br /></>}
                {invoice.sender.phone && <>Tel: {invoice.sender.phone}</>}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Empfänger" span={invoice.sender ? 1 : 2}>
              <strong>{invoice.guest?.name || 'Unbekannt'}</strong><br />
              {invoice.guest?.street && invoice.guest?.house_number && (
                <>{invoice.guest.street} {invoice.guest.house_number}<br /></>
              )}
              {invoice.guest?.address && <>{invoice.guest.address}<br /></>}
              {(invoice.guest?.zip || invoice.guest?.city) && (
                <>{invoice.guest.zip} {invoice.guest.city}<br /></>
              )}
              {invoice.guest?.country && <>{invoice.guest.country}<br /></>}
              {invoice.guest?.email && <>E-Mail: {invoice.guest.email}</>}
            </Descriptions.Item>
            {invoice.accommodation && (
              <Descriptions.Item label="Unterkunft" span={2}>
                {invoice.accommodation.name && <>{invoice.accommodation.name}<br /></>}
                {invoice.accommodation.checkin && (
                  <>Check-in: {dayjs(invoice.accommodation.checkin).format("DD.MM.YYYY")}<br /></>
                )}
                {invoice.accommodation.checkout && (
                  <>Check-out: {dayjs(invoice.accommodation.checkout).format("DD.MM.YYYY")}</>
                )}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Rechnungspositionen */}
        <Card title="Rechnungspositionen" style={{ marginBottom: 24 }}>
          <Table
            dataSource={invoice.items || []}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
            locale={{ emptyText: "Keine Positionen vorhanden" }}
          />
          
          {/* Summen */}
          <div className="invoice-totals">
            <table className="invoice-totals-table">
              <tbody>
                <tr>
                  <td>Zwischensumme:</td>
                  <td style={{ textAlign: "right" }}>{formatPrice(invoice.totals?.subtotal || 0)}</td>
                </tr>
                {invoice.totals?.tax7 > 0 && (
                  <tr>
                    <td>MwSt 7%:</td>
                    <td style={{ textAlign: "right" }}>{formatPrice(invoice.totals.tax7)}</td>
                  </tr>
                )}
                {invoice.totals?.tax19 > 0 && (
                  <tr>
                    <td>MwSt 19%:</td>
                    <td style={{ textAlign: "right" }}>{formatPrice(invoice.totals.tax19)}</td>
                  </tr>
                )}
                <tr style={{ fontWeight: "bold", fontSize: "1.1em" }}>
                  <td>Gesamtbetrag:</td>
                  <td style={{ textAlign: "right" }}>{formatPrice(invoice.totals?.total || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        {/* Rechnungstext */}
        {invoice.text && (
          <Card title="Rechnungstext" style={{ marginBottom: 24 }}>
            <Text style={{ whiteSpace: "pre-wrap" }}>{invoice.text}</Text>
          </Card>
        )}

        {/* Interne Notizen */}
        {invoice.notes && (
          <Card title="Interne Notizen" type="inner">
            <Text type="secondary" style={{ whiteSpace: "pre-wrap" }}>{invoice.notes}</Text>
          </Card>
        )}
      </div>
    </Show>
  );
}; 